import { TypographyParagraph, TypographyLabel } from '../../typography'
import { ImageIcon, VideoIcon, AudioIcon, FileIcon, OpenInNewIcon, FullScreenIcon } from '../../icons'
import { Button } from '../../buttons'
import { MediaAttestationVideo } from '../../content/Video'
import Image from '../../content/Image'
import Audio from '../../content/Audio'
import TypographyHeader from '#app/components/typography/TypographyHeading.tsx'
import { Modal } from '../../modalsAndOverlays'
import { useState } from 'react'

type MediaUploadProps = {
  metadata: {
    properties: {
      title?: string;
      description?: string;
      web2URL?: string;
      fileType?: string;
      fileExtension?: string;
      fileName?: string;
      pageCount?: number;
      fileSize?: string;
    };
  };
}

export default function MediaUpload({ metadata }: MediaUploadProps) {
  const { properties } = metadata;
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // Helper function to format file size
  const formatFileSize = (size?: string) => {
    if (!size) return '';
    const bytes = parseInt(size);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  return (
    <div className="flex flex-col space-y-2">
      {/* Media Title */}
      <div className="flex items-center space-x-2 text-textPrimaryLight dark:text-textPrimaryDark">
        <TypographyHeader size="small">
          {properties.title || 'Untitled Media'}
        </TypographyHeader>
      </div>

      {/* Media preview by media type */}
      <div className="mt-4 h-auto w-full rounded-lg">
        {/* video */}
        {properties.fileType === 'video' && properties.web2URL && (
					<div className="rounded-lg overflow-hidden">
						<MediaAttestationVideo
							videoUrl={properties.web2URL}
							title={properties.title || properties.fileName || ''}
						/>
					</div>
        )}

        {/* image */}
        {properties.fileType === 'image' && properties.web2URL && (					
					<div 
            className="rounded-lg overflow-hidden cursor-pointer transition-all duration-200 relative group"
            onClick={() => setIsImageModalOpen(true)}
          >
						<Image
							imageUrl={properties.web2URL}
							imageAlt={properties.title || properties.fileName || ''}
							containerClassName="rounded-lg overflow-hidden bg-uiPrimaryLight dark:bg-uiTertiaryDark hover:brightness-50"
            	className="w-full h-[180px] object-contain"
						/>
						<div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
							<div className="absolute inset-0 bg-black/30"></div>
							<FullScreenIcon size="large" className="text-white relative z-10" />
						</div>
					</div>
        )}

        {/* audio */}
        {properties.fileType === 'audio' && properties.web2URL && (
          <Audio
            className="h-12 w-full"
            containerClassName="w-full rounded-lg"
            audioURL={properties.web2URL}
          />
        )}

        {/* pdf */}
        {properties.fileExtension === 'pdf' && (
          <div className="group flex w-full rounded-md border border-textInactiveLight p-2 dark:border-textInactiveDark">
            <div className="mr-2 flex h-20 w-20 items-center justify-center bg-uiPrimaryLight dark:bg-uiPrimaryDark">
              <span className="relative inline-block">
                <FileIcon />
              </span>
            </div>
            <div className="mx-2 flex flex-grow flex-col justify-center gap-y-1 overflow-x-auto">
              <div className="flex items-center gap-x-1 text-textPrimaryLight dark:text-textPrimaryDark">
                <TypographyLabel
                  size="medium"
                  isBold={true}
                  additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark text-left"
                >
                  {properties.fileName}
                </TypographyLabel>
              </div>
              <TypographyLabel
                size="small"
                additionalClasses="mr-3 text-labelSmallSemiBold text-textSecondaryLight dark:text-textSecondaryDark truncate text-left"
              >
                {properties.pageCount} Pages • {properties.fileSize}
              </TypographyLabel>
            </div>
            <div className="flex flex-grow items-center justify-end text-grey500 group-hover:text-grey800 dark:text-grey400 dark:group-hover:text-white">
              <Button
                leftIcon={<OpenInNewIcon />}
                variant="primary"
                size="xSmall"
                onClick={() => {
                  window.open(properties.web2URL, '_blank')
                }}
              >
                View PDF
              </Button>
            </div>
          </div>
        )}
      </div>

			{/* Description */}
			<TypographyParagraph size="small" additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark mt-2">
				{properties.description?.slice(0, 116)}
				{properties.description && properties.description.length > 116 && '...'}
			</TypographyParagraph>

      {/* Image Modal */}
      <Modal
        size="xLarge"
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        body={
          <div className="flex justify-center items-center">
            <Image
              imageUrl={properties.web2URL}
              imageAlt={properties.title || properties.fileName || ''}
              containerClassName="rounded-lg overflow-hidden bg-uiPrimaryLight dark:bg-uiTertiaryDark"
              className="w-full h-auto object-contain"
            />
          </div>
        }
      />
    </div>
  );
} 